import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getConsentState, setConsent, CONSENT_EVENT } from '../analytics/consent';
import './CookieConsent.css';

/**
 * CookieConsent — San Tomas Tourism College
 * ---------------------------------------------------------------
 * Appears once, on first visit, until the visitor chooses. Accept and
 * Decline carry equal visual weight, which the Data Protection Act
 * requires: a greyed-out Decline beside a bright Accept is not a free
 * choice, and the ODPC has said so.
 *
 * Nothing loads from Meta or Google until Accept is pressed.
 *
 * Translated in all four languages the site ships — English, Kiswahili,
 * French and Spanish. Consent has to be *informed*, so a banner that
 * only speaks English to a visitor reading the site in Kiswahili isn't
 * really consent at all. Strings live in src/i18n.js under `consent`.
 */
const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    /* Wait a beat so the banner doesn't fight the hero slideshow. */
    const timer = setTimeout(() => {
      if (getConsentState() === null) setVisible(true);
    }, 900);

    /* "Cookie settings" in the footer clears the stored answer; listen so
       the banner reappears without a page reload. */
    const onChange = () => setVisible(getConsentState() === null);
    window.addEventListener(CONSENT_EVENT, onChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener(CONSENT_EVENT, onChange);
    };
  }, []);

  const choose = (granted) => {
    setConsent(granted);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="stc-consent"
      role="dialog"
      aria-live="polite"
      aria-label={t('consent.label', 'Tracking consent')}
    >
      <div className="stc-consent__inner">
        <div className="stc-consent__copy">
          <h2 className="stc-consent__title">
            {t('consent.title', 'Before you browse')}
          </h2>
          <p className="stc-consent__text">
            {t(
              'consent.body',
              'We use cookies from Meta and Google to see which of our adverts bring students to this site. They are not needed for the site to work, and your application details are never shared.'
            )}{' '}
            <Link to="/privacy" className="stc-consent__link">
              {t('consent.privacy', 'Read our privacy notice')}
            </Link>
            .
          </p>
        </div>

        <div className="stc-consent__actions">
          <button
            type="button"
            className="stc-consent__btn stc-consent__btn--decline"
            onClick={() => choose(false)}
          >
            {t('consent.decline', 'Decline')}
          </button>
          <button
            type="button"
            className="stc-consent__btn stc-consent__btn--accept"
            onClick={() => choose(true)}
          >
            {t('consent.accept', 'Accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
